import type {
  ConnectionState,
  Conversation,
  Participant,
  User,
} from "@twilio/conversations";
import { Client, Message } from "@twilio/conversations";
import { uniq } from "remeda";
import { create } from "zustand";

import {
  conversationsMap,
  mediaMap,
  messagesMap,
  participantsMap,
  usersMap,
} from "@/lib/messageCenter/conversationsObjects";
import { conversationSorter } from "@/lib/messageCenter/conversationsUtils";
import { normalizeMessage } from "@/lib/messageCenter/messagesUtils";
import { normalizeParticipant } from "@/lib/messageCenter/participantsUtils";
import type {
  ChatConversation,
  ChatMessage,
  ChatMessageLocal,
  ConversationsPaginator,
  MessageServiceStore,
} from "@/lib/messageCenter/types";

const useMessageService = create<MessageServiceStore>((set) => ({
  token: "",
  currentUserId: "",
  conversations: [],
  sid: "",
  messages: {},
  unreadMessages: {},
  users: {},
  participants: {},
  attachments: {},
  typingData: {},
  lastReadIndex: -1,
  client: null,
  connectionState: null,
  newMessage: null,
  conversationsPaginator: null,
  newMessageLocal: null,
  deleteMessageLocal: null,
  actions: {
    // Actions for token
    setToken: (token: string) => set({ token }),
    clearToken: () => set({ token: "", client: null }),
    // Actions for conversations
    setConversationsPaginator: (
      conversationsPaginator: ConversationsPaginator,
    ) => set({ conversationsPaginator }),
    setConversations: (conversations: ChatConversation[]) =>
      set({ conversations }),
    upsertConversation: (conversation: Conversation) =>
      set((state) => {
        const {
          sid,
          friendlyName,
          dateUpdated,
          lastReadMessageIndex,
          lastMessage,
        } = conversation;

        const updatedConversations = state.conversations
          .filter((c) => c.sid !== conversation.sid)
          .concat({
            sid,
            friendlyName,
            dateUpdated,
            lastReadMessageIndex,
            lastMessage: {
              ...lastMessage,
            },
          })
          .sort(conversationSorter);

        conversationsMap.set(sid, conversation);

        return { conversations: updatedConversations };
      }),
    updateConversation: (
      channelSid: string,
      parameters: Partial<ChatConversation>,
    ) =>
      set((state) => {
        const updatedConversations = state.conversations.map((conversation) =>
          conversation.sid === channelSid
            ? { ...conversation, ...parameters }
            : conversation,
        );
        return { conversations: updatedConversations };
      }),
    removeConversation: (conversationSid: string) =>
      set((state) => {
        const updatedConversations = state.conversations.filter(
          (conversation) => conversation.sid !== conversationSid,
        );
        return { conversations: updatedConversations };
      }),
    // Actions for SID
    updateCurrentConversation: (sid: string) => set({ sid }),
    // Actions for last read index
    setLastReadIndex: (lastReadIndex: number) => set({ lastReadIndex }),
    // Actions for messages
    pushMessages: (channelSid: string, messages: Message[]) =>
      set((state) => {
        const existingMessages = state.messages[channelSid] || [];

        for (const message of messages) {
          messagesMap.set(message.sid, message);
          if (message.attachedMedia) {
            message.attachedMedia.forEach((media) => {
              mediaMap.set(media.sid, media);
            });
          }
        }

        const updatedMessages = existingMessages.concat(
          messages.map(normalizeMessage),
        );

        return {
          messages: { ...state.messages, [channelSid]: updatedMessages },
        };
      }),
    addMessages: (channelSid: string, messages: (Message | ChatMessage)[]) =>
      set((state) => {
        const messagesToAdd = messages;
        const existingMessages = state.messages[channelSid] || [];

        const filteredExistingMessages = existingMessages.filter((message) => {
          return !messagesToAdd.find(
            (value) =>
              value.body === message.body &&
              value.author === message.author &&
              (message.index === -1 || value.index === message.index),
          );
        });

        const messagesUnique = [
          ...filteredExistingMessages,
          ...messagesToAdd.map(normalizeMessage),
        ];

        for (const message of messagesToAdd) {
          if (message instanceof Message) {
            messagesMap.set(message.sid, message);
            if (message.attachedMedia) {
              message.attachedMedia.forEach((media) => {
                mediaMap.set(media.sid, media);
              });
            }
          }
        }

        const sortedMessages = messagesUnique.sort((a, b) => a.index - b.index);

        return {
          messages: { ...state.messages, [channelSid]: sortedMessages },
        };
      }),
    removeMessages: (channelSid: string, messages: Message[]) =>
      set((state) => {
        const messagesToRemove = messages;
        const existingMessages = state.messages[channelSid] || [];

        const currentMessages = existingMessages.filter(
          ({ index }) =>
            !messagesToRemove.find(
              ({ index: messageIndex }) => messageIndex === index,
            ),
        );

        for (const message of messagesToRemove) {
          messagesMap.delete(message.sid);
          if (message.attachedMedia) {
            message.attachedMedia.forEach((media) => {
              mediaMap.delete(media.sid);
            });
          }
        }

        return {
          messages: { ...state.messages, [channelSid]: currentMessages },
        };
      }),
    // Actions for participants
    updateParticipants: (participants: Participant[], sid: string) =>
      set((state) => {
        for (const participant of participants) {
          participantsMap.set(participant.sid, participant);
        }

        const updatedParticipants = participants.map(normalizeParticipant);

        return {
          participants: {
            ...state.participants,
            [sid]: updatedParticipants,
          },
        };
      }),
    // Actions for users
    updateUser: (user: User) =>
      set((state) => {
        const { identity, friendlyName, attributes } = user;

        usersMap.set(identity, user);

        const updatedUsers = {
          ...state.users,
          [identity]: {
            identity,
            friendlyName: friendlyName ?? "",
            attributes,
          },
        };
        return { users: updatedUsers };
      }),
    // Actions for unread messages
    updateUnreadMessages: (channelSid: string, unreadCount: number) =>
      set((state) => {
        const updatedUnreadMessages = {
          ...state.unreadMessages,
          [channelSid]: unreadCount,
        };
        return { unreadMessages: updatedUnreadMessages };
      }),
    // Actions for attachments
    addAttachment: (
      channelSid: string,
      messageId: string,
      mediaSid: string,
      attachment: Blob,
    ) =>
      set((state) => {
        const updatedAttachments = {
          ...state.attachments,
          [channelSid]: {
            ...state.attachments[channelSid],
            [messageId]: {
              ...state.attachments[channelSid]?.[messageId],
              [mediaSid]: attachment,
            },
          },
        };
        return { attachments: updatedAttachments };
      }),
    clearAttachments: (channelSid: string, messageId: string) =>
      set((state) => {
        const updatedAttachments = {
          ...state.attachments,
          [channelSid]: {
            ...state.attachments?.[channelSid],
            [messageId]: {},
          },
        };
        return { attachments: updatedAttachments };
      }),
    // Actions for typing data
    typingStarted: (channelSid: string, participant: string) =>
      set((state) => {
        const existedUsers = state.typingData[channelSid] || [];
        existedUsers.push(participant);
        const updatedTypingData = {
          ...state.typingData,
          [channelSid]: uniq(existedUsers),
        };
        return { typingData: updatedTypingData };
      }),
    typingEnded: (channelSid: string, participant: string) =>
      set((state) => {
        const filteredUsers = (state.typingData[channelSid] || []).filter(
          (user) => user !== participant,
        );
        const updatedTypingData = {
          ...state.typingData,
          [channelSid]: filteredUsers,
        };
        return { typingData: updatedTypingData };
      }),
    // Actions for connection state
    setConnectionState: (connectionState: ConnectionState) =>
      set({ connectionState }),
    // Actions for current user id
    setCurrentUserId: (currentUserId: string) => set({ currentUserId }),
    // Actions for client
    setClient: (token: string) => {
      const client = new Client(token);
      set({ client });
    },
    // Actions for messages local and server
    setNewMessage: (newMessage: Message | null) => {
      set({ newMessage });
    },
    setNewMessageLocal: (newMessageLocal: ChatMessageLocal | null) => {
      set({ newMessageLocal });
    },
    setDeleteMessageLocal: (deleteMessageLocal: string | null) => {
      set({ deleteMessageLocal });
    },
  },
}));

// Custom hooks for state selection
export const useToken = () => useMessageService((state) => state.token);
export const useCurrentUserId = () =>
  useMessageService((state) => state.currentUserId);
export const useConversations = () =>
  useMessageService((state) => state.conversations);
export const useSid = () => useMessageService((state) => state.sid);
export const useMessages = () => useMessageService((state) => state.messages);
export const useUnreadMessages = () =>
  useMessageService((state) => state.unreadMessages);
export const useParticipants = () =>
  useMessageService((state) => state.participants);
export const useUsers = () => useMessageService((state) => state.users);
export const useAttachments = () =>
  useMessageService((state) => state.attachments);
export const useTypingData = () =>
  useMessageService((state) => state.typingData);
export const useLastReadIndex = () =>
  useMessageService((state) => state.lastReadIndex);
export const useClient = () => useMessageService((state) => state.client);
export const useConnectionState = () =>
  useMessageService((state) => state.connectionState);
export const useNewMessage = () =>
  useMessageService((state) => state.newMessage);
export const useConversationsPaginator = () =>
  useMessageService((state) => state.conversationsPaginator);
export const useNewMessageLocal = () =>
  useMessageService((state) => state.newMessageLocal);
export const useDeleteMessageLocal = () =>
  useMessageService((state) => state.deleteMessageLocal);

// Custom hooks for actions
export const useMessageServiceActions = () =>
  useMessageService((state) => state.actions);
